<template>
  <div>
    <b-row class="no-print">
      <b-col class="d-flex flex-row align-items-center mb-2">
        <h2 class="mr-2 mb-0">
          {{ $t('costResume') }}
        </h2>
        <b-button
          size="sm"
          class="d-flex align-items-center"
          variant="outline-primary"
          @click="showTable = !showTable"
        >
          <feather-icon
            :icon="showTable?'EyeIcon':'EyeOffIcon'"
            class="mr-1"
          />
          <span>
            {{ showTable ? $t('show'):$t('hide') }}
          </span>
        </b-button>
      </b-col>
    </b-row>
    <b-row
      :class="{ 'd-none': showTable }"
      class="no-print"
    >
      <b-col
        sm="12"
        class="print"
      >
        <b-card
          class=""
          no-body
        >
          <b-table
            :no-border-collapse="true"
            responsive
            :items="items"
            :fields="fields"
            class="mb-0 print-table print-cost-resume-matrix"
          >
            <template #cell()="data">
              <template v-if="data.field.key === 'material'">
                {{ data.value }}
              </template>
              <template v-else>
                <span class="price-cell-value">
                  {{ formatPrice(getRawCellValue(data)) }}
                </span>
              </template>
            </template>
          </b-table>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  components: {},
  props: {
    quoteItems: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      items: [],
      fields: [],
      showTable: true,
    }
  },
  computed: {
    ...mapGetters('app', ['currentBreakPoint', 'isSmallScreen']),
  },
  watch: {
    quoteItems: {
      handler(value) {
        const supplierDataByKey = {}
        const supplierOrder = []
        const items = []
        let fallbackSupplierIndex = 1
        const userData = this.safeUserData()
        const scopedRoles = Array.isArray(userData.scoped_roles) ? userData.scoped_roles : []

        const resolveSupplierName = price => {
          const supplierId = price?.supplier_branch_id
          const roleMatch = scopedRoles.find(role => role.role_resource_id === supplierId)
          const nameFromRole = roleMatch?.role_resource_name
          const name = nameFromRole
            || price?.supplier_branch_name
            || price?.supplier_name
            || price?.branch_name
            || price?.business_name
            || `Proveedor ${fallbackSupplierIndex}`

          if (!nameFromRole
            && !price?.supplier_branch_name
            && !price?.supplier_name
            && !price?.branch_name
            && !price?.business_name
          ) {
            fallbackSupplierIndex += 1
          }

          return name
        }

        value.forEach((item, productIndex) => {
          const productRow = {
            material: item?.product_catalog?.name || `Material ${productIndex + 1}`,
          }
          const unitPrices = Array.isArray(item?.unit_prices) ? item.unit_prices : []

          unitPrices.forEach(price => {
            const supplierName = resolveSupplierName(price)
            const supplierKey = price?.supplier_branch_id
              ? `supplier_${price.supplier_branch_id}`
              : `supplier_name_${this.normalizeSupplierKey(supplierName)}`

            if (!supplierDataByKey[supplierKey]) {
              supplierDataByKey[supplierKey] = {
                label: supplierName,
              }
              supplierOrder.push(supplierKey)
            }

            const numericUnitPrice = Number(price?.unit_price)
            productRow[supplierKey] = Number.isFinite(numericUnitPrice) && numericUnitPrice > -1
              ? numericUnitPrice
              : null
          })

          productRow.winnerSupplierKey = this.resolveWinnerSupplierKey(item, supplierDataByKey, supplierOrder)

          items.push(productRow)
        })

        const normalizedItems = items.map(row => {
          const normalizedRow = { ...row }
          supplierOrder.forEach(supplierKey => {
            if (!Object.prototype.hasOwnProperty.call(normalizedRow, supplierKey)) {
              normalizedRow[supplierKey] = null
            }
          })
          return normalizedRow
        })

        const fields = [
          {
            key: 'material',
            label: this.$t('products'),
            thStyle: { width: '24%' },
            thClass: 'align-middle',
            tdClass: 'text-nowrap',
          },
          ...supplierOrder.map(supplierKey => ({
            key: supplierKey,
            label: this.formatSupplierHeaderLabel(supplierDataByKey[supplierKey].label),
            tdClass: (cellValue, key, itemRow) => this.getSupplierCellClass(cellValue, key, itemRow),
            thClass: 'text-center align-middle text-wrap',
          })),
        ]

        this.fields = fields
        this.items = normalizedItems
      },
      immediate: true,
    },
  },
  mounted() {},
  methods: {
    getSelectedSupplierLabel(item) {
      return item?.selected_supplier_branch_name
        || item?.selected_supplier_name
        || item?.supplier_name
        || item?.branch_name
        || item?.business_name
        || item?.supplier
        || ''
    },
    resolveWinnerSupplierKey(item, supplierDataByKey, supplierOrder) {
      if (!item) return null

      const selectedId = item.selected_supplier_branch_id
      const selectedLabel = this.getSelectedSupplierLabel(item)
      if (selectedId) {
        const keyById = `supplier_${selectedId}`
        if (supplierDataByKey[keyById]) return keyById

        if (selectedLabel) {
          const normalizedSelected = this.normalizeSupplierKey(selectedLabel)
          const matchedKey = Object.keys(supplierDataByKey).find(
            key => this.normalizeSupplierKey(supplierDataByKey[key].label) === normalizedSelected,
          )
          if (matchedKey) return matchedKey
        }

        supplierDataByKey[keyById] = { label: selectedLabel || `Proveedor ${supplierOrder.length + 1}` }
        supplierOrder.push(keyById)
        return keyById
      }

      if (!selectedLabel) return null

      const normalizedSelected = this.normalizeSupplierKey(selectedLabel)
      const keyByName = `supplier_name_${normalizedSelected}`
      if (supplierDataByKey[keyByName]) return keyByName

      const matchedKey = Object.keys(supplierDataByKey).find(
        key => this.normalizeSupplierKey(supplierDataByKey[key].label) === normalizedSelected,
      )
      return matchedKey || null
    },
    getRawCellValue(data) {
      if (!data || !data.item || !data.field || !data.field.key) return data ? data.value : null
      return data.item[data.field.key]
    },
    formatSupplierHeaderLabel(label) {
      const words = String(label || '').trim().split(/\s+/).filter(Boolean)
      if (!words.length) return ''

      const lines = []
      let currentLine = ''
      const maxLineChars = 11

      words.forEach(word => {
        const nextLine = currentLine ? `${currentLine} ${word}` : word
        if (nextLine.length > maxLineChars && currentLine) {
          lines.push(currentLine)
          currentLine = word
        } else {
          currentLine = nextLine
        }
      })

      if (currentLine) lines.push(currentLine)
      return lines.slice(0, 4).join('\n')
    },
    formatPrice(value) {
      if (value === null || value === undefined || value === '') return '-'
      const numericValue = Number(value)
      if (!Number.isFinite(numericValue) || numericValue <= 0) return '-'

      try {
        return `$ ${this.$options.filters.money(numericValue)} MXN`
      } catch (e) {
        return `$ ${new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(numericValue)} MXN`
      }
    },
    getSupplierCellClass(value, supplierKey, item) {
      const classes = ['price-cell']
      if (value === null || value === undefined || value === '') {
        classes.push('empty-price')
        return classes.join(' ')
      }
      const numericValue = Number(value)

      if (!Number.isFinite(numericValue) || numericValue <= 0) {
        classes.push('empty-price')
        return classes.join(' ')
      }

      if (item && item.winnerSupplierKey === supplierKey) {
        classes.push('best-price')
      }

      return classes.join(' ')
    },
    normalizeSupplierKey(value) {
      return String(value || '')
        .toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/[^a-z0-9_]/g, '')
    },
    safeUserData() {
      try {
        return JSON.parse(localStorage.getItem('userData')) || {}
      } catch (e) {
        return {}
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.no-print { display: block; }
.only-print { display: none; }

.print-cost-resume-matrix thead th {
  background-color: #F3F2F7;
  color: #6E6B7B;
  border-color: #EBE9F1;
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: 0.35px;
  text-transform: uppercase;
  white-space: normal;
  word-break: break-word;
  line-height: 1.2;
}

.print-cost-resume-matrix td {
  border-color: #EBE9F1;
  color: #6E6B7B;
  font-size: 0.98rem;
  vertical-align: middle;
}

.print-cost-resume-matrix td.price-cell {
  text-align: right;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.print-cost-resume-matrix td.price-cell.empty-price {
  text-align: center;
  color: #98A2B3;
}

.print-cost-resume-matrix td.price-cell.best-price {
  background-color: #ECFDF3;
  color: #027A48;
  font-weight: 600;
}

.print-cost-resume-matrix .price-cell-value {
  display: inline-block;
}

.print-cost-resume-matrix td.price-cell.best-price .price-cell-value {
  text-decoration: underline;
  text-decoration-thickness: 1.5px;
  text-underline-offset: 2px;
}

.print-cost-resume-matrix tbody tr:nth-child(even) td {
  background: #FAFBFC;
}

.print-cost-resume-matrix tbody tr td.price-cell.best-price {
  background-color: #ECFDF3;
}

@media print {
  .no-print { display: block !important; }
  .only-print { display: none !important; }

  .print-title {
    font-size: 12pt;
    font-weight: 700;
    margin-bottom: 6px;
  }

  .print-subtitle {
    font-size: 9.5pt;
    color: #667085;
    margin-bottom: 10px;
  }

  .print-group-start td {
    border-top: 2px solid #D0D5DD;
  }

  .print-group-start td:first-child {
    font-weight: 700;
  }

  .print {
    overflow: visible;
  }
  .print-table {
    overflow-x: visible;
  }
  .noprint {
    display: none;
  }
}
</style>
